import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usersApi } from '../../api/modules/users';
import { ApiError } from '../../api/core';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

type LinkStatus = 'loading' | 'success' | 'error';

export const StudioLink: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<LinkStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const studioId = id?.trim();
    if (!studioId) {
      setStatus('error');
      setErrorMessage('Missing Compete Studio account id.');
      return;
    }

    const returnPath = `/studiolink/${studioId}`;
    let cancelled = false;

    (async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          try {
            const u = JSON.parse(userJson) as { emailVerified?: boolean; isAdmin?: boolean };
            if (!u.isAdmin && u.emailVerified === false) {
              try {
                sessionStorage.setItem('afterEmailVerify', returnPath);
              } catch {
                /* ignore */
              }
              navigate('/verify-email', { replace: true });
              return;
            }
          } catch {
            /* ignore parse errors */
          }
        }

        const linkedUser = await usersApi.linkCompeteStudio(studioId);
        if (cancelled) return;

        try {
          localStorage.setItem('user', JSON.stringify(linkedUser));
        } catch {
          /* ignore */
        }

        setStatus('success');
      } catch (error) {
        if (cancelled) return;

        if (error instanceof ApiError && error.status === 401) {
          navigate(`/login?redirect=${encodeURIComponent(returnPath)}`, { replace: true });
          return;
        }

        setStatus('error');
        if (error instanceof ApiError) {
          setErrorMessage(error.message || 'Failed to link Compete Studio account.');
        } else {
          setErrorMessage('Failed to link Compete Studio account.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-primary-600 animate-spin" aria-hidden />
            <h1 className="text-2xl font-bold text-gray-900">Linking your accounts…</h1>
            <p className="text-sm text-gray-600">Please wait a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="mx-auto h-12 w-12 text-green-600" aria-hidden />
            <h1 className="text-2xl font-bold text-gray-900">Success!</h1>
            <p className="text-sm text-gray-600">
              Your Compete Studio and Compete Pages accounts are now linked.
            </p>
            <Link to="/dashboard" className="btn-primary inline-flex">
              Go to dashboard
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="mx-auto h-12 w-12 text-red-500" aria-hidden />
            <h1 className="text-2xl font-bold text-gray-900">Couldn’t link accounts</h1>
            <p className="text-sm text-red-600">{errorMessage}</p>
            <Link to="/dashboard" className="btn-primary inline-flex">
              Go to dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
